/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Flex,
  ModalOverlay,
  Select,
  Spacer,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import validator from 'validator'
import { changeProfile, putLocation, putProfile, putProfileImage } from '../../api/user/home/home'
import { AuthContext } from '../../App'
import { countries } from '../countries/countries'
import useMobile from '../../hooks/useMobile'
import { scrollbarStyle } from '../../theme/scrollbarStyles'
import CustomTabs from '../input/CustomTabs'
import ImageSelect from '../input/ImageSelect'
import ProfileCropModal from './ProfileCropModal'

import { Geolocation } from '@capacitor/geolocation'
import { Capacitor } from '@capacitor/core'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { requestHealthKitAuthorization } from '../../hooks/useHealthKitSync'
import api from '../../api/api'
import { formatAmericanPhoneNumber, formatUniversalPhoneNumber } from '../../pages/auth/Register'

interface UserOnboardingModalProps {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

const ContactSection = ({
  contact,
  setContact,
  phoneError,
}: {
  contact: {
    phone_number: string
    phone_number_prefix: string
    emergency_contact_name: string
    emergency_contact_phone_number: string
    emergency_contact_phone_number_prefix: string
  }
  setContact: React.Dispatch<
    React.SetStateAction<{
      phone_number: string
      phone_number_prefix: string
      emergency_contact_name: string
      emergency_contact_phone_number: string
      emergency_contact_phone_number_prefix: string
    }>
  >
  phoneError: boolean
}) => {
  return (
    <>
      <Text variant="title" fontSize="md" lineHeight="19px" mt="10px" mb="16px">
        Please provide your contact details (required for social sign-up).
      </Text>

      <FormControl mb="20px" isInvalid={phoneError} isRequired>
        <FormLabel>Personal phone number</FormLabel>
        <Flex gap="5px" flexWrap="wrap">
          <Select
            w="140px"
            size="lg"
            name="phone_number_prefix"
            borderRadius="0"
            borderColor="#DCE2EA"
            borderWidth="1.5px"
            value={contact.phone_number_prefix}
            onChange={(e) => setContact((prev) => ({ ...prev, phone_number_prefix: e.target.value }))}
          >
            {countries.map((country: any, index: number) => (
              <option key={index} value={country.dial_code}>
                {country.dial_code} ({country.name})
              </option>
            ))}
          </Select>
          <Input
            flex="1"
            minW="160px"
            name="phone_number"
            size="lg"
            placeholder="(954) 444-4444"
            type="tel"
            value={formatAmericanPhoneNumber(contact.phone_number, contact.phone_number_prefix)}
            onChange={(e) => {
              const digits = formatUniversalPhoneNumber(e.target.value)
              setContact((prev) => ({ ...prev, phone_number: digits }))
            }}
          />
        </Flex>
        <FormErrorMessage>Please enter a valid phone number</FormErrorMessage>
      </FormControl>

      <FormControl mb="20px">
        <FormLabel>Emergency contact name</FormLabel>
        <Input
          name="emergency_contact_name"
          size="lg"
          placeholder="Emergency contact name"
          type="text"
          value={contact.emergency_contact_name}
          onChange={(e) => setContact((prev) => ({ ...prev, emergency_contact_name: e.target.value }))}
        />
      </FormControl>

      <FormControl mb="20px">
        <FormLabel>Emergency contact phone number</FormLabel>
        <Flex gap="5px" flexWrap="wrap">
          <Select
            w="140px"
            size="lg"
            name="emergency_contact_phone_number_prefix"
            borderRadius="0"
            borderColor="#DCE2EA"
            borderWidth="1.5px"
            value={contact.emergency_contact_phone_number_prefix}
            onChange={(e) =>
              setContact((prev) => ({ ...prev, emergency_contact_phone_number_prefix: e.target.value }))
            }
          >
            {countries.map((country: any, index: number) => (
              <option key={index} value={country.dial_code}>
                {country.dial_code} ({country.name})
              </option>
            ))}
          </Select>
          <Input
            flex="1"
            minW="160px"
            name="emergency_contact_phone_number"
            size="lg"
            placeholder="(954) 444-4444"
            type="tel"
            value={formatAmericanPhoneNumber(
              contact.emergency_contact_phone_number,
              contact.emergency_contact_phone_number_prefix
            )}
            onChange={(e) => {
              const digits = formatUniversalPhoneNumber(e.target.value)
              setContact((prev) => ({ ...prev, emergency_contact_phone_number: digits }))
            }}
          />
        </Flex>
      </FormControl>
    </>
  )
}

const QuestionaireSection = ({
  data,
  setData,
}: {
  data: {
    level: number
    gender: number
    competitionLevel: number
    playLength: number
    racquetSportsPast: number
    matchesPlayed: number
    lessonsReceived: number
    age: number
    fitnessLevel: number
    tennisForm: number
  }
  setData: React.Dispatch<
    React.SetStateAction<{
      level: number
      gender: number
      competitionLevel: number
      playLength: number
      racquetSportsPast: number
      matchesPlayed: number
      lessonsReceived: number
      age: number
      fitnessLevel: number
      tennisForm: number
    }>
  >
}) => {
  return (
    <>
      <Text variant="title" fontSize="md" lineHeight="19px" mt="25px" mb="20px">
        At what level do you think you are?
      </Text>
      <CustomTabs
        name="level"
        value={data.level}
        onChange={(v) => setData((prevData) => ({ ...prevData, level: v }))}
        choices={['Beginner', 'Intermediate', 'Intermediate High', 'Advanced', 'Advanced 4.5+', 'Mixed-Level']}
      />

      <Text variant="title" fontSize="md" lineHeight="19px" mt="25px" mb="20px">
        Are you male or female?
      </Text>
      <CustomTabs
        name="gender"
        value={data.gender}
        onChange={(v) => setData((prevData) => ({ ...prevData, gender: v }))}
        choices={['Male', 'Female', 'Non-binary']}
      />

      {/* <Text variant='title' fontSize='md' lineHeight='19px' mt='25px' mb='20px'>
                What is the highest level you have competed at?          
            </Text>
            <CustomTabs
                name='competitionLevel'
                value={data.competitionLevel}
                onChange={(v) => setData((prevData) => ({ ...prevData, competitionLevel: v }))}
                choices={['National','Regional','Club','Recreational only']}
            />

            <Text variant='title' fontSize='md' lineHeight='19px' mt='25px' mb='20px'>
                How long have you been playing tennis regularly?
            </Text>
            <CustomTabs
                name='playLength'
                value={data.playLength}
                onChange={(v) => setData((prevData) => ({ ...prevData, playLength: v }))}
                choices={['Less than a year','Less than 2 years','More than 2 years']}
            />

            <Text variant='title' fontSize='md' lineHeight='19px' mt='25px' mb='20px'>
                Have you played other racquet sports in the past?       
            </Text>
            <CustomTabs
                name='racquetSportsPast'
                value={data.racquetSportsPast}
                onChange={(v) => setData((prevData) => ({ ...prevData, racquetSportsPast: v }))}
                choices={['No, never','Yes']}
            />

            <Text variant='title' fontSize='md' lineHeight='19px' mt='25px' mb='20px'>
                How many matches have you played per month over the last year?   
            </Text>
            <CustomTabs
                name='matchesPlayed'
                value={data.matchesPlayed}
                onChange={(v) => setData((prevData) => ({ ...prevData, matchesPlayed: v }))}
                choices={['Less than four matches per month, never','More than 4 matches per month']}
            />

            <Text variant='title' fontSize='md' lineHeight='19px' mt='25px' mb='20px'>
                Have you received tennis lessons in the last year?   
            </Text>
            <CustomTabs
                name='lessonsReceived'
                value={data.lessonsReceived}
                onChange={(v) => setData((prevData) => ({ ...prevData, lessonsReceived: v }))}
                choices={['No','Yes, one month','Yes, two months', 'Yes, one year', 'Yes, more than two years']}
            /> */}

      <Text variant="title" fontSize="md" lineHeight="19px" mt="25px" mb="20px">
        How old are you?
      </Text>
      <CustomTabs
        name="age"
        value={data.age}
        onChange={(v) => setData((prevData) => ({ ...prevData, age: v }))}
        choices={['18-30', '30-40', '40-50', 'Over 50']}
      />

      {/* <Text variant='title' fontSize='md' lineHeight='19px' mt='25px' mb='20px'>
                What is your fitness level?
            </Text>
            <CustomTabs
                name='fitnessLevel'
                value={data.fitnessLevel}
                onChange={(v) => setData((prevData) => ({ ...prevData, fitnessLevel: v }))}
                choices={['Excellent','Good','Normal','Low','Very low']}
            /> */}

      <Text variant="title" fontSize="md" lineHeight="19px" mt="25px" mb="20px">
        Racket sport of choice?
      </Text>
      <CustomTabs
        name="tennisForm"
        value={data.tennisForm}
        onChange={(v) => setData((prevData) => ({ ...prevData, tennisForm: v }))}
        choices={['Padel', 'Pickleball', 'Tennis', 'All']}
      />
    </>
  )
}
const UserDetailSection = ({
  avatar,
  setAvatar,
  bio,
  setBio,
  avatarError,
}: {
  avatar: File | null
  setAvatar: React.Dispatch<React.SetStateAction<File | null>>
  bio: string
  setBio: React.Dispatch<React.SetStateAction<string>>
  avatarError: boolean
}) => {
  return (
    <>
      <Text variant="title" fontSize="md" lineHeight="19px" mt="30px" mb="4px">
        Upload a profile picture
      </Text>
      <Text fontSize="sm" color="#6B7A8D" mb="10px">
        Add a photo so your coach can recognise you
      </Text>
      <FormControl isInvalid={avatarError} isRequired>
        <ImageSelect image={avatar} onChange={(v) => setAvatar(v)} />
        <FormErrorMessage>A profile picture is required</FormErrorMessage>
      </FormControl>

      <Text variant="title" fontSize="md" lineHeight="19px" mt="25px" mb="10px">
        Create a bio for your profile
      </Text>

      <Textarea
        sx={scrollbarStyle}
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        placeholder="Type anything..."
        fontSize="15px"
        fontWeight="400"
        lineHeight="21px"
        borderRadius="4px"
        bg="transparent"
        borderColor="#DCE2EA"
        borderWidth="1.5px"
        _placeholder={{ color: '#171C22', opacity: '40%' }}
        rows={5}
        resize="none"
      />
    </>
  )
}
const LocationServicesSection = () => {
  const isMobile = useMobile(600)

  return (
    <Center mt="20px" mb="10px">
      <Box mt={isMobile ? '60%' : '0'} bgImage="/components/location_services.svg" w="126px" h="126px" />
    </Center>
  )
}

const HealthKitStep = ({ onNext }: { onNext: () => void }) => {
  const queryClient = useQueryClient()
  const [isConnecting, setIsConnecting] = useState(false)

  const handleConnect = async () => {
    setIsConnecting(true)
    const success = await requestHealthKitAuthorization()
    if (success) {
      try {
        await api.put('member/myprofile/', { healthkit_connected: true })
        queryClient.invalidateQueries(['auth'])
      } catch (e) {
        // Best-effort
      }
    }
    setIsConnecting(false)
    onNext()
  }

  return (
    <Box textAlign="center" py="20px">
      <Text fontSize="36px" mb="12px">❤️‍🔥</Text>
      <Text fontSize="16px" fontWeight="600" mb="8px">
        Track your activity when you play
      </Text>
      <Text fontSize="14px" color="gray.500" mb="24px">
        Connect health tracking to see calories, steps, and distance after every event.
      </Text>
      <Button
        w="100%"
        bg="black"
        color="white"
        borderRadius="10px"
        fontSize="15px"
        fontWeight="600"
        mb="8px"
        _hover={{ bg: 'gray.800' }}
        onClick={handleConnect}
        isLoading={isConnecting}
      >
        Connect Health Tracking
      </Button>
      <Button
        w="100%"
        variant="ghost"
        color="gray.400"
        fontSize="13px"
        onClick={onNext}
      >
        Skip
      </Button>
    </Box>
  )
}

const UserOnboardingModal = (props: UserOnboardingModalProps) => {
  const isMobile = useMobile(600)

  const isSocialLoginUser =
    typeof window !== 'undefined' && localStorage.getItem('signUpWithSocial') === '1'

  const isNativePlatform = Capacitor.isNativePlatform()

  const { user } = useContext(AuthContext)

  const [contact, setContact] = useState({
    phone_number: '',
    phone_number_prefix: '+1',
    emergency_contact_name: '',
    emergency_contact_phone_number: '',
    emergency_contact_phone_number_prefix: '+1',
  })
  const [phoneError, setPhoneError] = useState(false)
  const [avatarError, setAvatarError] = useState(false)

  useEffect(() => {
    if (phoneError) setPhoneError(false)
  }, [contact.phone_number, contact.phone_number_prefix])

  // -------- GEOLOCATION PERMISSIONS --------

  const sendLocation = useMutation(['location'], putLocation)

  const requestLocation = async () => {
    try {
      const { location } = await Geolocation.checkPermissions()
      if (location === 'denied') return

      if (location !== 'granted') {
        const { location } = await Geolocation.requestPermissions()
        if (location === 'denied') return
      }

      const { coords } = await Geolocation.getCurrentPosition()

      sendLocation.mutate({
        lat: coords.latitude,
        lng: coords.longitude,
      })
    } catch {
      // Geolocation not available on this platform
    }
  }

  // useEffect(() => {
  //     if (user != null && !user.is_staff) {
  //     requestLocation()
  //     }
  // },[])

  const [step, setStep] = useState(0)

  //const [skipped,setSkipped] = useState(false)

  const [avatar, setAvatar] = useState<File | null>(null)
  const [bio, setBio] = useState('')

  useEffect(() => {
    if (avatarError && avatar) setAvatarError(false)
  }, [avatar])
  const [questionaire, setQuestionaire] = useState({
    level: -1,
    gender: -1,
    competitionLevel: -1,
    playLength: -1,
    racquetSportsPast: -1,
    matchesPlayed: -1,
    lessonsReceived: -1,
    age: -1,
    fitnessLevel: -1,
    tennisForm: -1,
  })

  const queryClient = useQueryClient()

  const updateProfile = useMutation(['putProfile'], putProfile)
  const changeProfileMutation = useMutation(['changeProfile'], changeProfile)

  const updateAvatar = useMutation(['putProfileImage'], putProfileImage, {})

  //const updateBanner = useMutation(["putBanner"], putBanner)

  // useEffect(() => {

  //     updateAvatar.mutate({
  //         profile_image : avatar,
  //     })

  // }, [avatar])

  const { isOpen, onClose, onOpen } = useDisclosure()

  useEffect(() => {
    if (avatar != null) {
      isOpen ? onClose() : onOpen()

      //updateProfile.mutate({ profile_image : avatar })
    }
  }, [avatar])

  const totalSteps = isSocialLoginUser ? (isNativePlatform ? 5 : 4) : (isNativePlatform ? 4 : 3)
  const isLocationStep = isNativePlatform ? step === totalSteps - 2 : step === totalSteps - 1
  const isHealthKitStep = isNativePlatform && step === totalSteps - 1

  const buildContactPayload = () =>
    isSocialLoginUser
      ? {
        phone_number: contact.phone_number_prefix + contact.phone_number,
        emergency_contact_name: contact.emergency_contact_name || undefined,
        emergency_contact_phone_number:
          contact.emergency_contact_phone_number_prefix + contact.emergency_contact_phone_number || undefined,
      }
      : {}

  const buildQuestionnairePayload = () => ({
    skill_level: questionaire.level === -1 ? null : questionaire.level,
    comp_level: questionaire.competitionLevel === -1 ? null : questionaire.competitionLevel,
    experience: questionaire.playLength === -1 ? null : questionaire.playLength,
    gender:
      questionaire.gender === -1 ? null : questionaire.gender === 0 ? 'M' : questionaire.gender === 1 ? 'F' : 'X',
    has_past_racquet_experience: questionaire.racquetSportsPast === -1 ? null : questionaire.racquetSportsPast,
    game_frequency: questionaire.matchesPlayed === -1 ? null : questionaire.matchesPlayed === 1 ? 4 : 0,
    lessons:
      questionaire.lessonsReceived === -1
        ? null
        : questionaire.lessonsReceived === 0
          ? 0
          : questionaire.lessonsReceived === 1
            ? 1
            : questionaire.lessonsReceived === 2
              ? 2
              : questionaire.lessonsReceived === 3
                ? 12
                : 24,
    age_range:
      questionaire.age === -1
        ? null
        : questionaire.age === 0
          ? 18
          : questionaire.age === 1
            ? 30
            : questionaire.age === 2
              ? 40
              : 50,
    fitness_level: questionaire.fitnessLevel === -1 ? null : questionaire.fitnessLevel,
    form_of_tennis:
      questionaire.tennisForm === -1
        ? null
        : questionaire.tennisForm === 0
          ? 'PAD'
          : questionaire.tennisForm === 1
            ? 'PIC'
            : questionaire.tennisForm === 2
              ? 'TEN'
              : 'ALL',
    bio: bio,
    ...buildContactPayload(),
  })

  const isUserDetailStep = isSocialLoginUser ? step === 2 : step === 1

  const handleSkip = () => {
    if (isUserDetailStep && !avatar) {
      setAvatarError(true)
      return
    }
    updateProfile.mutate(buildQuestionnairePayload(), {
      onSuccess: () => {
        queryClient.invalidateQueries(['auth'])
        props.onClose()
      },
    })
  }

  const [statusBarHeight, setStatusBarHeight] = useState<number>(0)

  useEffect(() => {
    SafeArea.getStatusBarHeight().then(({ statusBarHeight }) => {
      setStatusBarHeight(statusBarHeight)
    })
  }, [])

  return (
    <>
      <ProfileCropModal
        image={avatar ? URL.createObjectURL(avatar) : ''}
        isOpen={isOpen}
        onClose={onClose}
        onSave={(file: File | null) => {
          setAvatar(file)
          updateAvatar.mutate({
            profile_image: file,
          })
        }}
        isLoading={updateAvatar.isLoading}
      />
      <Modal
        isOpen={props.isOpen}
        onClose={props.onClose}
        size={isMobile ? 'full' : 'lg'}
        scrollBehavior="inside"
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent>
          <Spacer h={statusBarHeight + 'px'} userSelect="none" />

          <ModalHeader ml="3px" mt={statusBarHeight + 'px'} fontSize={isMobile ? '22px' : '34px'}>
            Hey {user?.display_name}!
          </ModalHeader>
          <ModalBody sx={scrollbarStyle}>
            <Text variant="subtle" fontSize="lg" lineHeight="21px" mb="12px">
              Complete your profile to make the most of your LVBL experience.
            </Text>

            {isSocialLoginUser && step === 0 ? (
              <ContactSection contact={contact} setContact={setContact} phoneError={phoneError} />
            ) : (isSocialLoginUser ? step === 1 : step === 0) ? (
              <QuestionaireSection data={questionaire} setData={setQuestionaire} />
            ) : (isSocialLoginUser ? step === 2 : step === 1) ? (
              <UserDetailSection avatar={avatar} setAvatar={setAvatar} bio={bio} setBio={setBio} avatarError={avatarError} />
            ) : isHealthKitStep ? (
              <HealthKitStep onNext={() => {
                props.onClose()
              }} />
            ) : (
              <LocationServicesSection />
            )}
          </ModalBody>
          <ModalFooter gap="5px" justifyContent={(isLocationStep || isHealthKitStep) ? 'center' : 'space-between'}>
            {(isLocationStep || isHealthKitStep) ? (
              <></>
            ) : (
              <Button
                variant="gost"
                size="sm"
                onClick={handleSkip}
                color="#3B4654"
                isDisabled={isSocialLoginUser && step === 0}
              >
                Skip
              </Button>
            )}
            {!isHealthKitStep && (
              <Button
                variant="heavy"
                size="sm"
                p="8px 20px"
                gap="4px"
                onClick={() => {
                  if (isSocialLoginUser && step === 0) {
                    const fullPhoneString =
                      contact.phone_number_prefix +
                      formatUniversalPhoneNumber(
                        formatAmericanPhoneNumber(contact.phone_number, contact.phone_number_prefix)
                      )
                    const valid = validator.isMobilePhone(fullPhoneString)
                    setPhoneError(!valid)
                    if (!valid) return
                    updateProfile.mutate(buildContactPayload())
                    if (typeof window !== 'undefined') localStorage.removeItem('signUpWithSocial')
                    setStep(1)
                  } else if (isSocialLoginUser ? step === 1 : step === 0) {
                    updateProfile.mutate(buildQuestionnairePayload())
                    setStep(step + 1)
                  } else if (isLocationStep) {
                    requestLocation()
                    queryClient.invalidateQueries('auth')
                    if (isNativePlatform) {
                      setStep(step + 1)
                    } else {
                      props.onClose()
                    }
                  } else {
                    if (!avatar) {
                      setAvatarError(true)
                      return
                    }
                    changeProfileMutation.mutate({ bio: bio })
                    setStep(step + 1)
                  }
                }}
                leftIcon={<Text color="#FFFFFF99">{step + 1}/{totalSteps}</Text>}
              >
                {isLocationStep ? 'Enable Location Services' : 'Continue'}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default UserOnboardingModal
